import { QueryClientProvider } from '@tanstack/react-query';
import React, { useContext, useMemo } from 'react';

import { canadaCoreApis, createCoreRegionalApis } from '@/apiClients/coreRegionalApis';
import type { RegionalApis } from '@/apiClients/regionalApis';
import { getRegionQueryClient } from '@/apiClients/regionQueryClients';
import { Capabilities } from '@/auth-capabilities';
import { RegionalAuthErrorBanner } from '@/components/RegionalAuthErrorBanner';
import { Alert } from '@/components/ui';
import { ApiRegion, config } from '@/config';
import { useAuth } from '@/context/appContext';

/**
 * The queue shell's default is the data-review client only. `FullRegionalApis` replaces this with the
 * food, meal and staff clients on every other route, before those pages render. The type stays the full
 * set so call sites are unchanged; the queue does not read the clients it does not have.
 */
export const RegionalApisContext = React.createContext<RegionalApis>(canadaCoreApis as RegionalApis);

export type RegionalApiFactory = (region: ApiRegion, canadaAccessToken: string) => RegionalApis;

/** Queue pages build a data-review client. Other routes install `createRegionalApis` in its place. */
export const RegionalApiFactoryContext = React.createContext<RegionalApiFactory>(
  createCoreRegionalApis as RegionalApiFactory,
);

/** Outside a RegionScope this is Canada, so every existing Canada page is unchanged. */
export const useRegionalApis = () => useContext(RegionalApisContext);

export const RegionScope = (props: {
  region: ApiRegion;
  children: React.ReactNode;
  // Row-level scopes (one per list row) set this to false: the page renders a single
  // banner above the list, so one US failure does not repeat on every row.
  showAlerts?: boolean;
}) => {
  const { region, children, showAlerts = true } = props;
  const { authInfo, hasAuth } = useAuth();
  const accessToken = authInfo?.access_token;
  const ambient = useRegionalApis();
  const createApis = useContext(RegionalApiFactoryContext);
  const regionalApis = useMemo(
    () => (region === ApiRegion.CA || !accessToken ? null : createApis(region, accessToken)),
    [createApis, region, accessToken],
  );

  if (region === ApiRegion.CA) {
    // A Canada scope keeps the clients already in context: the queue shell's data-review
    // client, or the full set once FullRegionalApis has installed it. Calling the factory
    // here would rebuild them and drop a test double installed only for the US branch.
    const canada = ambient.region === ApiRegion.CA ? ambient : createApis(ApiRegion.CA, accessToken ?? '');
    return <RegionalApisContext.Provider value={canada}>{children}</RegionalApisContext.Provider>;
  }
  if (config.usRegionalMutationsBlocked) {
    if (!showAlerts) {
      return null;
    }
    return (
      <Alert role="alert" tone="caution">
        {hasAuth(Capabilities.commonDataReviewerAdmin)
          ? 'The US API points at the Canada host, so US review is off here. Set VITE_US_API_URL, or set VITE_DUAL_REGION_LOCAL=true for local dual-region testing.'
          : 'This review item is unavailable here.'}
      </Alert>
    );
  }
  if (!regionalApis) {
    return null;
  }
  return (
    <RegionalApisContext.Provider value={regionalApis}>
      <QueryClientProvider client={getRegionQueryClient(region)}>
        {showAlerts && <RegionalAuthErrorBanner />}
        {children}
      </QueryClientProvider>
    </RegionalApisContext.Provider>
  );
};
